import type { HttpTypes } from "@medusajs/types"
import type {
  CartDrawerLine,
  CartDrawerViewModel,
  CartSnapshot,
} from "./types"

const numeric = (value: unknown): number => {
  if (value && typeof value === "object" && "value" in value) {
    return Number((value as { value?: unknown }).value ?? 0)
  }
  return Number(value ?? 0)
}

const roundMoney = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100

type NativePresentedLineItem = HttpTypes.StoreCartLineItem & {
  adjustments?: Array<{ promotion_id?: string | null; amount?: unknown; code?: unknown }>
  total?: unknown
  compare_at_unit_price?: unknown
}

const normalizeSelectedOptions = (options: any[] = []) =>
  options
    .map((option) => ({
      name: String(option?.option?.title ?? "Seçenek"),
      value: String(option?.value ?? "").trim(),
    }))
    .filter((option) => option.value && option.value.toLowerCase() !== "default")

const nativePromotionPresentation = (
  item: HttpTypes.StoreCartLineItem
): { labels: string[]; freeQuantity: number } => {
  const labels = new Set<string>()
  let freeQuantity = 0
  const unitPrice = numeric(item.unit_price)

  const presented = item as NativePresentedLineItem
  for (const adjustment of presented.adjustments ?? []) {
    if (!adjustment?.promotion_id) continue
    const amount = numeric(adjustment.amount)
    const code = String(adjustment.code ?? "").trim()
    if (amount > 0 && code) labels.add(code)
    if (amount > 0 && unitPrice > 0) {
      const exact = amount / unitPrice
      if (Number.isInteger(exact) && exact > 0) freeQuantity += exact
    }
  }

  return {
    labels: [...labels].sort((a, b) => a.localeCompare(b, "tr")),
    freeQuantity,
  }
}

const createLine = (item: HttpTypes.StoreCartLineItem): CartDrawerLine | null => {
  const variantId = item.variant_id
  if (!variantId) return null

  const quantity = numeric(item.quantity)
  const effectiveTotal = roundMoney(numeric((item as NativePresentedLineItem).total))
  const unitPrice = numeric(item.unit_price)
  const compareAt = numeric((item as NativePresentedLineItem).compare_at_unit_price ?? item.unit_price)
  const compareAtTotal = roundMoney(Math.max(unitPrice, compareAt) * quantity)
  const promotion = nativePromotionPresentation(item)

  return {
    key: item.id,
    lineId: item.id,
    productId: item.product_id ?? null,
    variantId,
    quantity,
    maxQuantity: Math.max(99, quantity),
    title: String(
      (item.product?.metadata as Record<string, unknown> | null | undefined)?.short_name ??
        (item.metadata as Record<string, unknown> | null | undefined)?.short_name ??
        item.product_title ??
        item.title
    ),
    catalogTitle: String(item.product_title ?? item.title),
    handle: item.product_handle ?? null,
    thumbnail: item.thumbnail ?? null,
    options: normalizeSelectedOptions((item.variant as any)?.options ?? []),
    promotionLabels: promotion.labels,
    freeQuantity: promotion.freeQuantity,
    effectiveTotal,
    compareAtTotal,
    savings: roundMoney(Math.max(0, compareAtTotal - effectiveTotal)),
  }
}

export function buildCartDrawerViewModel(
  snapshot: CartSnapshot
): CartDrawerViewModel {
  const lines = (snapshot.cart?.items ?? [])
    .map(createLine)
    .filter((line): line is CartDrawerLine => Boolean(line))

  return {
    lines,
    itemCount: lines.reduce((sum, line) => sum + line.quantity, 0),
    total: roundMoney(numeric(snapshot.cart?.total)),
    savingsTotal: roundMoney(lines.reduce((sum, line) => sum + line.savings, 0)),
    currencyCode: snapshot.cart?.currency_code ?? "try",
    campaign: snapshot.presentation?.campaign
      ? {
          label: snapshot.presentation.campaign.label,
          endsAt: snapshot.presentation.campaign.ends_at,
        }
      : null,
    incentive: snapshot.presentation?.incentive ?? null,
  }
}
